<script setup>
import { computed } from 'vue';

const props = defineProps({
 field: {
 type: Object,
 required: true,
 },
 selected: {
 type: Boolean,
 default: false,
 },
});

const fieldTypeIcon = computed(() => {
 const map = {
 text: 'i-lucide-type',
 email: 'i-lucide-mail',
 phone: 'i-lucide-phone',
 number: 'i-lucide-hash',
 date: 'i-lucide-calendar',
 select: 'i-lucide-chevron-down',
 multi_select: 'i-lucide-list-checks',
 radio: 'i-lucide-circle-dot',
 checkbox: 'i-lucide-check-square',
 textarea: 'i-lucide-align-left',
 file: 'i-lucide-upload',
 heading: 'i-lucide-heading',
 paragraph: 'i-lucide-pilcrow',
 divider: 'i-lucide-minus',
 rating: 'i-lucide-star',
 scale: 'i-lucide-gauge',
 hidden: 'i-lucide-eye-off',
 cpf: 'i-lucide-fingerprint',
 cnpj: 'i-lucide-building-2',
 cep: 'i-lucide-map-pin',
 currency: 'i-lucide-coins',
 };
 return map[props.field.type] || 'i-lucide-square';
});

const isLayoutField = computed(() => {
 return ['heading', 'paragraph', 'divider'].includes(props.field.type);
});

const isChoiceField = computed(() => {
 return ['select', 'multi_select', 'radio', 'checkbox'].includes(
 props.field.type
 );
});

const widthClass = computed(() => {
 return props.field.width === 'half' ? 'w-1/2' : 'w-full';
});
</script>

<template>
 <div :class="widthClass" class="p-1">
 <div class="px-3 py-2">
 <!-- Heading -->
 <template v-if="field.type === 'heading'">
 <h3
 class="text-base font-semibold text-n-slate-12"
 >
 {{ field.content || field.label }}
 </h3>
 </template>

 <!-- Paragraph -->
 <template v-else-if="field.type === 'paragraph'">
 <p
 class="text-sm text-n-slate-11 !mb-0"
 >
 {{ field.content || field.label }}
 </p>
 </template>

 <!-- Divider -->
 <template v-else-if="field.type === 'divider'">
 <hr
 class="border-t border-n-weak my-2"
 />
 </template>

 <!-- Hidden field -->
 <template v-else-if="field.type === 'hidden'">
 <div
 class="flex items-center gap-2 px-3 py-2 bg-n-slate-2/50 rounded-lg border border-dashed border-n-strong"
 >
 <span
 class="i-lucide-eye-off size-3.5 text-n-slate-9"
 />
 <span
 class="text-xs text-n-slate-9"
 >
 Campo oculto: {{ field.name || field.label }}
 </span>
 </div>
 </template>

 <!-- Regular form fields -->
 <template v-else>
 <!-- Label -->
 <label
 class="block text-sm font-medium text-n-slate-11 mb-1"
 >
 {{ field.label }}
 <span v-if="field.required" class="text-red-500 ml-0.5"
 >*</span
 >
 </label>

 <!-- Help text -->
 <p
 v-if="field.help_text"
 class="text-xs text-n-slate-9 !mb-1.5"
 >
 {{ field.help_text }}
 </p>

 <!-- Text / Email / Phone / Number / Date input -->
 <template
 v-if="
 ['text', 'email', 'phone', 'number', 'date', 'cpf', 'cnpj', 'cep', 'currency'].includes(
 field.type
 )
 "
 >
 <div
 class="flex items-center h-9 px-3 rounded-lg border border-n-weak bg-n-slate-2/50"
 >
 <span
 :class="fieldTypeIcon"
 class="size-3.5 text-n-slate-9 mr-2 shrink-0"
 />
 <span
 class="text-sm text-n-slate-9"
 >
 {{ field.placeholder || 'Digite aqui...' }}
 </span>
 </div>
 </template>

 <!-- Textarea -->
 <template v-else-if="field.type === 'textarea'">
 <div
 class="h-20 px-3 py-2 rounded-lg border border-n-weak bg-n-slate-2/50"
 >
 <span
 class="text-sm text-n-slate-9"
 >
 {{ field.placeholder || 'Escreva aqui...' }}
 </span>
 </div>
 </template>

 <!-- Select -->
 <template v-else-if="field.type === 'select'">
 <div
 class="flex items-center justify-between h-9 px-3 rounded-lg border border-n-weak bg-n-slate-2/50"
 >
 <span
 class="text-sm text-n-slate-9"
 >
 {{ field.placeholder || 'Selecione...' }}
 </span>
 <span
 class="i-lucide-chevron-down size-3.5 text-n-slate-9"
 />
 </div>
 </template>

 <!-- Multi Select -->
 <template v-else-if="field.type === 'multi_select'">
 <div
 class="flex items-center justify-between h-9 px-3 rounded-lg border border-n-weak bg-n-slate-2/50"
 >
 <span
 class="text-sm text-n-slate-9"
 >
 {{ field.placeholder || 'Selecione opcoes...' }}
 </span>
 <span
 class="i-lucide-chevron-down size-3.5 text-n-slate-9"
 />
 </div>
 <div
 v-if="field.options?.length"
 class="flex flex-wrap gap-1 mt-1.5"
 >
 <span
 v-for="opt in field.options.slice(0, 3)"
 :key="opt"
 class="text-[10px] px-1.5 py-0.5 rounded bg-n-slate-3 text-n-slate-10"
 >
 {{ opt }}
 </span>
 </div>
 </template>

 <!-- Radio -->
 <template v-else-if="field.type === 'radio'">
 <div class="space-y-1.5">
 <div
 v-for="opt in (field.options || []).slice(0, 4)"
 :key="opt"
 class="flex items-center gap-2"
 >
 <div
 class="size-4 rounded-full border-2 border-n-strong"
 />
 <span
 class="text-sm text-n-slate-11"
 >
 {{ opt }}
 </span>
 </div>
 </div>
 </template>

 <!-- Checkbox -->
 <template v-else-if="field.type === 'checkbox'">
 <div class="space-y-1.5">
 <div
 v-for="opt in (field.options || []).slice(0, 4)"
 :key="opt"
 class="flex items-center gap-2"
 >
 <div
 class="size-4 rounded border-2 border-n-strong"
 />
 <span
 class="text-sm text-n-slate-11"
 >
 {{ opt }}
 </span>
 </div>
 </div>
 </template>

 <!-- File upload -->
 <template v-else-if="field.type === 'file'">
 <div
 class="flex flex-col items-center justify-center h-20 rounded-lg border-2 border-dashed border-n-weak bg-n-slate-2/50"
 >
 <span
 class="i-lucide-upload size-5 text-n-slate-9 mb-1"
 />
 <span
 class="text-xs text-n-slate-9"
 >
 Arraste ou clique para enviar
 </span>
 </div>
 </template>

 <!-- Rating -->
 <template v-else-if="field.type === 'rating'">
 <div class="flex items-center gap-1">
 <span
 v-for="n in field.maxRating || 5"
 :key="n"
 class="i-lucide-star size-5 text-n-slate-7"
 />
 </div>
 </template>

 <!-- Scale -->
 <template v-else-if="field.type === 'scale'">
 <div class="flex items-center gap-2">
 <span
 class="text-xs text-n-slate-9 shrink-0"
 >
 {{ field.minLabel || field.minValue || 0 }}
 </span>
 <div class="flex-1 flex items-center gap-1">
 <div
 v-for="n in (field.maxValue || 10) -
 (field.minValue || 0) +
 1"
 :key="n"
 class="flex-1 h-8 rounded border border-n-weak bg-n-slate-2/50 flex items-center justify-center"
 >
 <span
 class="text-xs text-n-slate-9"
 >
 {{ (field.minValue || 0) + n - 1 }}
 </span>
 </div>
 </div>
 <span
 class="text-xs text-n-slate-9 shrink-0"
 >
 {{ field.maxLabel || field.maxValue || 10 }}
 </span>
 </div>
 </template>
 </template>
 </div>
 </div>
</template>
